import React, { useState } from 'react';

import Breadcrumb from '@/components/Breadcrumb/Breadcrumb';
import SubPageHero from '@/components/SubPageHero/SubPageHero';
import StackedContent from '@/components/StackedContent/StackedContent';
import TimeCommitmentCard from '@/components/TimeCommitmentCard/TimeCommitmentCard';
import Card from '@/components/Card/Card';
import heroPageIcon from '@/assets/images/backpack_school_item.svg';
import coachHelpingImg from '@/assets/images/lc-helping.svg';
import cardMediaCommunity from '@/assets/images/k12_community_img_square.svg';
import { Image } from 'react-bootstrap';
import { getCommunityUrl } from '@/utils/linksUtils';
import { useSelectedStudent } from '@/context/SelectedStudentContext';
import { getContentByGrade } from '@/utils/getContentByGradeUtils';
import VidyardPlayer from '@/components/VidyardPlayer/VidyardPlayer';
import { useOverviewContext } from '@/context/OverviewContext';
import { useToolSettings } from '@/context/ToolSettingsContext';
import { getActiveToolIDs, shouldShowLearningCoachCommunity } from '@/utils/toolSettingsUtils';
import CommunityCard from '@/components/CommunityCard/CommunityCard';

const BeingLearningCoach = () => {
    const { userRoleVal, loggedInUser = null } = useOverviewContext();
    const { selectedStudent } = useSelectedStudent();
    const studentGrade = selectedStudent?.gradeId; 
    const studentDisplayName = selectedStudent?.nickName  || selectedStudent?.firstName ;
    const lcRoleSchoolContent = studentGrade ? getContentByGrade(studentGrade, 'section-school-content') : null;
    const lcSchoolContent = lcRoleSchoolContent
    ? {
        ...lcRoleSchoolContent,
        header: lcRoleSchoolContent.header.replace('{{studentName}}', studentDisplayName ?? 'Student'),
        }
    : null;
    const { toolSettings } = useToolSettings();
    const activeToolIDs = getActiveToolIDs(toolSettings);
    const showLearningCoachCommunity = shouldShowLearningCoachCommunity(userRoleVal, activeToolIDs);
    const communityLink = getCommunityUrl();
    const breadcrumbItems = [
        { title: "Home", path: "/" },
        { title: "School Information", path: "/schoolinfo" },
        { title: "The Learning Coach Role" }
    ];
   
    return (
    <>
    <div className="lc-role-ols-container">
        <div className="inner-container">
            <Breadcrumb items={breadcrumbItems} />
            <div className="subpage-hero-container">
                <SubPageHero
                    theme="secondary"
                    title="The Learning Coach Role"
                    description="A Learning Coach is a parent, relative, sibling, legal guardian, or other responsible adult who supports a student's online education journey at home."
                    image={heroPageIcon}
                />
            </div>
        </div>

        {/* Section:2 */}
        <StackedContent>
            <div className="coach-teacher-row">
            <StackedContent.Col1 cols="5" desktopOrder="2">
                <Image src={coachHelpingImg} fluid={true} alt="Learning Coach" />
            </StackedContent.Col1>
            <StackedContent.Col2 cols="5" desktopOrder="1">
                <h2 className="heading-2 coach-teacher-label">Learning Coach vs. Teacher</h2>
                <p className="content-block">
                    As a Learning Coach, you'll help your child with all life's little details, just as you would while caring for them while they're not in school. But you'll also partner with state-certified teachers to create an inspiring, customized learning experience in which your student can thrive.
                </p>
                <p className="content-block">
                    Here's a quick explanation of the roles teachers and Learning Coaches play:
                </p>
            </StackedContent.Col2>
            </div>
        </StackedContent>

        <section className="coach-vs-teacher-section">
        <StackedContent>
            <div className="coach-teacher-row">
            <StackedContent.Col1 cols="5">
                <div className="coach-teacher-block">
                    <h3 className="heading-3">Learning Coaches...</h3>
                    <div className="content-block">
                        <ul className="list-block">
                            <li>Facilitate monitor lessons</li>
                            <li>Make sure students attend virtual classes</li>
                            <li>Help students track and submit work</li>
                            <li>Assist with questions about lessons</li>
                            <li>Maintain communication with teachers</li>
                        </ul>
                    </div>
                    
                </div>
            </StackedContent.Col1>
            <StackedContent.Col2 cols="5">
                <div className="coach-teacher-block">
                    <h3 className="heading-3">Teachers...</h3>
                    <div className="content-block">
                        <ul className="list-block">
                            <li>Set lesson plans</li>
                            <li>Host live, direct instruction sessions</li>
                            <li>Grade and assess student performance</li>
                            <li>Provide differentiated instruction as needed</li>
                            <li>Host office hours or check-in times</li>
                        </ul>
                    </div>
                </div>
            </StackedContent.Col2>
            </div>
        </StackedContent>
        </section>

        {/* Section:3 Time Commitment Card (Grade-based) */}
        {studentGrade && (
            <section className="section-time-commitment">
                <TimeCommitmentCard grade={studentGrade} />
            </section>
        )}

        {/* Section:4 School Content (Grade-based) */}
        <section className="section-school-content">
        {lcSchoolContent ? (
            <StackedContent>
                <StackedContent.Col1 cols="10" desktopOrder="2">
                    <h2 className="heading-2">{lcSchoolContent.header}</h2>
                    <p className="content-block">
                        {lcSchoolContent.description}
                    </p>

                    <div className="video-wrapper">
                    {lcSchoolContent?.vidyardUuid && (
                        <VidyardPlayer key={lcSchoolContent.vidyardUuid} uuid={lcSchoolContent.vidyardUuid} />
                    )}
                    </div>

                    {lcSchoolContent.additionalContent}
                </StackedContent.Col1>
            </StackedContent>
        ) : (
            <div className="section-school-content--empty" aria-hidden="true" />
        )}
        </section>
        
        {/* section:5 Horizontal Card */}
        <section className="section-community-h-card">
        {loggedInUser?.role === "LEARNING_COACH" && showLearningCoachCommunity && (
            <StackedContent>
                <StackedContent.Col1 cols="10">
                    <CommunityCard
                        type="learningCoach"
                        linkUrl={communityLink}
                        orientation="horizontal"
                        hideEyebrow={true}
                        themeOverride="primary-light"
                        descriptionOverride="When you step up to be a Learning Coach, you get to share in the joys and challenges of the learning process, supporting the growth and discovery of your student. You can also connect with local Learning Coaches to share advice and learn from peers—check out the Friends Near Me map! "
                        mediaOverride={cardMediaCommunity}
                    />
                </StackedContent.Col1>
            </StackedContent>
        )}
        </section>

    </div>

    </>
  );

};

export default BeingLearningCoach;
